import React, { useState, useCallback, useEffect } from "react";
import { Grid, Button, Box, Typography, Chip } from "@mui/material";
import UserFormModal from "./creation";
import UserList from "./user_list";
import RoleChartModal from "./role_chart";
import { makeStyles } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import { showSnackbar } from "../../redux/snackbar/actions";
import { showLoader, hideLoader } from "../../redux/loader/actions";
import userApi from "../../services/users";

const useStyles = makeStyles((theme) => ({
  animatedButton: {
    background: "linear-gradient(45deg, #ff416c 30%, #ff4b2b 90%)",
    color: "#fff",
    padding: "10px 30px",
    fontSize: "16px",
    fontWeight: "bold",
    textTransform: "none",
    borderRadius: "5px",
    transition: "transform 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
      background: "linear-gradient(45deg, #ff4b2b 30%, #ff416c 90%)",
    },

    [theme.breakpoints.down("sm")]: {
      padding: "0px 8px",
      fontSize: "14px",
      width: "100%",
    },
  },
  chip: {
    marginRight: "16px",
  },
}));

const UserManagement = () => {
  const menuItems = useSelector((state) => state.menuReducer.items);
  const createMenuItem = menuItems?.find(
    (item) => item.name.toLowerCase() === "users"
  );
  const isDisabled = createMenuItem?.access_type === "read_write";
  const classes = useStyles();
  const dispatch = useDispatch();
  const [users, setUsers] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [userFormModalOpen, setUserFormModalOpen] = useState(false);
  const [roleChartModalOpen, setRoleChartModalOpen] = useState(false);
  const [lockedRoles, setLockedRoles] = useState({});
  const [statusRoles, setStatusRoles] = useState({});
  const [userData, setUserData] = useState({});
  const [roleChart, setRoleChart] = useState([]);
  const [userList, setUserList] = useState([]);
  const [userDatabyID, setUserDatabyID] = useState([]);
  const [mode, setMode] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [searchparams, setSearchparams] = useState();
  const organizationItems = useSelector(
    (state) => state.organizationReducer.items
  );

  const handleAddUser = (user) => {
    if (editIndex !== null) {
      const updatedUsers = users.map((u, index) =>
        index === editIndex ? user : u
      );
      setUsers(updatedUsers);
      setEditIndex(null);
    } else {
      setUsers([...users, user]);
    }
    setUserFormModalOpen(false);
  };

  const handleEditUser = (index) => {
    setEditIndex(index);
    setUserFormModalOpen(true);
  };

  const handleDeleteUser = (index) => {
    setUsers(users.filter((_, i) => i !== index));
  };

  const handleToggleStatus = (index) => {
    setStatusRoles((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleToggleLock = (index) => {
    setLockedRoles((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const fetchRoleChart = useCallback(async () => {
    dispatch(showLoader());
    try {
      const response = await userApi.getAllRoles();

      const responseData = response?.data?.data;
      if (responseData) {
        setRoleChart(responseData);
      } else {
        console.warn("Unexpected response format:", responseData);
      }
    } catch (error) {
      console.error("Error fetching pricing:", error);
    } finally {
      dispatch(hideLoader());
    }
  }, [dispatch]);

  const editUserById = async (userId) => {
    dispatch(showLoader());
    try {
      const res = await userApi.getUserById(userId);
      if (res?.data?.status) {
        setUserDatabyID(res.data.data);
        setMode("edit");
        setUserFormModalOpen(true);
      } else {
        console.error("Error fetching user data");
      }
    } catch (error) {
      console.error("Error fetching user:", error);
    } finally {
      dispatch(hideLoader());
    }
  };
  const fetchUsers = useCallback(async () => {
    dispatch(showLoader());
    try {
      const response = await userApi.getAllUsers(
        paginationModel.page + 1,
        paginationModel.pageSize,
        searchparams
      );
      if (response?.data?.status) {
        setUserList(response.data.data || []);
        setRowCount(response.data.pagination.totalItems || 0);
      } else {
        setUserList([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      dispatch(hideLoader());
    }
  }, [paginationModel.page, paginationModel.pageSize, searchparams, dispatch]);

  useEffect(() => {
    fetchUsers();
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    searchparams,
    dispatch,
    fetchUsers,
    organizationItems,
  ]);

  return (
    <>
      <Grid item xs={12} mb={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">User Management</Typography>
          <Box display="flex" alignItems="center">
            <Chip
              label="Role Chart"
              color="primary"
              variant="outlined"
              onClick={() => setRoleChartModalOpen(true)}
              className={classes.chip}
              sx={{ marginRight: "10px" }}
            />
            {isDisabled && (
              <Button
                variant="contained"
                className={classes.animatedButton}
                onClick={() => setUserFormModalOpen(true)}
              >
                Add User
              </Button>
            )}
          </Box>
        </Box>
      </Grid>
      <UserList
        users={userData}
        onEditUser={handleEditUser}
        onDeleteUser={handleDeleteUser}
        lockedRoles={lockedRoles}
        handleToggleStatus={handleToggleStatus}
        handleToggleLock={handleToggleLock}
        setUserList={setUserList}
        userList={userList}
        userDatabyID={userDatabyID}
        setUserDatabyID={setUserDatabyID}
        editUserById={editUserById}
        rowCount={rowCount}
        setSearchparams={setSearchparams}
        paginationModel={paginationModel}
        setPaginationModel={setPaginationModel}
        fetchUsers={fetchUsers}
      />
      <UserFormModal
        open={userFormModalOpen}
        onClose={() => setUserFormModalOpen(false)}
        onSave={handleAddUser}
        existingUser={editIndex !== null ? users[editIndex] : null}
        userDatabyID={userDatabyID}
        mode={mode}
        setMode={setMode}
        fetchUsers={fetchUsers}
        setUserDatabyID={setUserDatabyID}
      />
      <RoleChartModal
        open={roleChartModalOpen}
        onClose={() => setRoleChartModalOpen(false)}
        fetchRoleChart={fetchRoleChart}
        roleChart={roleChart}
      />
    </>
  );
};

export default UserManagement;
